import React, { useEffect, useMemo, useState } from 'react'
import api from '../../services/api'
import '../../css/mediaConverter.css'

const IMAGE_EXTENSIONS = new Set(['heic', 'heif', 'avif', 'webp', 'jpg', 'jpeg', 'png'])
const AUDIO_EXTENSIONS = new Set(['mpeg', 'mpg', 'mp2', 'mp3', 'wav'])

function extensionOf(fileName = '') {
  return fileName.split('.').pop()?.toLowerCase() ?? ''
}

function normalizeFormat(format) {
  if (format === 'jpeg') return 'jpg'
  if (format === 'heif') return 'heic'
  if (format === 'mpg' || format === 'mp2') return 'mpeg'
  return format
}

function kindOf(file) {
  const ext = extensionOf(file?.name)
  if (IMAGE_EXTENSIONS.has(ext)) return 'image'
  if (AUDIO_EXTENSIONS.has(ext)) return 'audio'
  return null
}

function targetLabel(target) {
  const labels = {
    heic: 'HEIC',
    avif: 'AVIF',
    webp: 'WebP',
    jpg: 'JPEG',
    png: 'PNG',
    mpeg: 'MPEG Audio (Layer II)',
    mp3: 'MP3',
    wav: 'WAV',
  }
  return labels[target] ?? target.toUpperCase()
}

async function readApiError(error) {
  const payload = error?.response?.data
  if (payload instanceof Blob) {
    try {
      return JSON.parse(await payload.text())?.message ?? 'Conversione non riuscita.'
    } catch {
      return 'Conversione non riuscita.'
    }
  }
  return payload?.message ?? payload?.error ?? 'Conversione non riuscita.'
}

function filenameFromDisposition(header, fallback) {
  if (!header) return fallback
  const utf = header.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf) return decodeURIComponent(utf[1])
  const plain = header.match(/filename="?([^";]+)"?/i)
  return plain?.[1] ?? fallback
}

export default function MediaConverterPage() {
  const [capabilities, setCapabilities] = useState(null)
  const [file, setFile] = useState(null)
  const [target, setTarget] = useState('')
  const [quality, setQuality] = useState(82)
  const [converting, setConverting] = useState(false)
  const [message, setMessage] = useState(null)

  useEffect(() => {
    let mounted = true
    api.get('/media-converter/capabilities')
      .then(({ data }) => {
        if (mounted) setCapabilities(data)
      })
      .catch(() => {
        if (mounted) setMessage({ type: 'error', text: 'Impossibile caricare il convertitore.' })
      })
    return () => { mounted = false }
  }, [])

  const kind = kindOf(file)
  const inputFormat = normalizeFormat(extensionOf(file?.name))
  const targets = useMemo(() => {
    const values = kind ? capabilities?.[kind]?.output ?? [] : []
    return values.filter((value) => normalizeFormat(value) !== inputFormat)
  }, [capabilities, kind, inputFormat])

  const onFileChange = (event) => {
    const selected = event.target.files?.[0] ?? null
    setMessage(null)
    setFile(selected)
    setTarget('')

    if (!selected) return
    const selectedKind = kindOf(selected)
    if (!selectedKind) {
      setMessage({ type: 'error', text: 'Formato non supportato.' })
      return
    }
    const maxBytes = (capabilities?.limits?.maxUploadMb ?? 18) * 1024 * 1024
    if (selected.size > maxBytes) {
      setMessage({
        type: 'error',
        text: `Il file supera il limite di ${capabilities?.limits?.maxUploadMb ?? 18} MB.`,
      })
      return
    }
    const candidates = capabilities?.[selectedKind]?.output ?? []
    const first = candidates.find((value) => normalizeFormat(value) !== normalizeFormat(extensionOf(selected.name)))
    setTarget(first ?? '')
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!file || !kind || !target) return

    setConverting(true)
    setMessage({ type: 'info', text: 'Conversione in corso. Non chiudere la pagina…' })

    try {
      const form = new FormData()
      form.append('file', file)
      form.append('target', target)
      form.append('quality', String(quality))

      const response = await api.post('/media-converter/convert', form, { responseType: 'blob' })
      const fallbackName = `${file.name.replace(/\.[^.]+$/, '')}.${target}`
      const downloadName = filenameFromDisposition(response.headers['content-disposition'], fallbackName)
      const blobUrl = URL.createObjectURL(response.data)
      const anchor = document.createElement('a')
      anchor.href = blobUrl
      anchor.download = downloadName
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1_000)
      setMessage({ type: 'ok', text: `Conversione completata: ${downloadName}` })
    } catch (error) {
      setMessage({ type: 'error', text: await readApiError(error) })
    } finally {
      setConverting(false)
    }
  }

  const limits = capabilities?.limits

  return (
    <main className="mc-wrap">
      <header className="mc-header">
        <div>
          <h1>Media Converter</h1>
          <p>Conversioni isolate e temporanee: il file originale non viene mai modificato.</p>
        </div>
        <span className={`mc-status ${capabilities?.availableSlots > 0 ? 'mc-status--ready' : 'mc-status--busy'}`}>
          {capabilities?.availableSlots > 0 ? 'Disponibile' : 'Occupato'}
        </span>
      </header>

      {message && <div className={`mc-alert mc-alert--${message.type}`}>{message.text}</div>}

      <section className="mc-card">
        <form onSubmit={handleSubmit} className="mc-form">
          <label className="mc-field">
            <span>File da convertire</span>
            <input
              type="file"
              accept=".heic,.heif,.avif,.webp,.jpg,.jpeg,.png,.mpeg,.mpg,.mp2,.mp3,.wav"
              onChange={onFileChange}
              disabled={converting}
            />
            <small>Immagini: HEIC, AVIF, WebP, JPEG, PNG · Audio: MPEG, MP3, WAV</small>
          </label>

          {file && kind && (
            <div className="mc-file-summary">
              <i className={kind === 'image' ? 'fas fa-image' : 'fas fa-wave-square'} />
              <div>
                <strong>{file.name}</strong>
                <span>{(file.size / 1024 / 1024).toFixed(2)} MB · {kind === 'image' ? 'immagine' : 'audio'}</span>
              </div>
            </div>
          )}

          <label className="mc-field">
            <span>Formato di destinazione</span>
            <select
              value={target}
              onChange={(event) => setTarget(event.target.value)}
              disabled={!kind || converting}
              required
            >
              <option value="">Seleziona…</option>
              {targets.map((value) => (
                <option key={value} value={value}>{targetLabel(value)}</option>
              ))}
            </select>
          </label>

          {kind === 'image' && target !== 'png' && (
            <label className="mc-field">
              <span>Qualità: {quality}</span>
              <input
                type="range"
                min="40"
                max="95"
                value={quality}
                onChange={(event) => setQuality(Number(event.target.value))}
                disabled={converting}
              />
            </label>
          )}

          <button
            type="submit"
            className="mc-submit"
            disabled={!file || !kind || !target || converting || capabilities?.availableSlots === 0}
          >
            <i className={converting ? 'fas fa-spinner fa-spin' : 'fas fa-arrows-rotate'} />
            {converting ? 'Conversione…' : 'Converti e scarica'}
          </button>
        </form>
      </section>

      <section className="mc-safety-grid" aria-label="Limiti di sicurezza">
        <article>
          <i className="fas fa-microchip" />
          <h2>Risorse limitate</h2>
          <p>Una conversione alla volta, un thread, priorità bassa e timeout automatici.</p>
        </article>
        <article>
          <i className="fas fa-gauge-high" />
          <h2>Limiti iniziali</h2>
          <p>
            Upload {limits?.maxUploadMb ?? 18} MB · immagini fino a {Math.round((limits?.maxImagePixels ?? 40000000) / 1000000)} MP ·
            audio fino a {Math.floor((limits?.maxAudioSeconds ?? 900) / 60)} minuti.
          </p>
        </article>
      </section>
    </main>
  )
}
